import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PlusIcon, CheckIcon, LinkIcon, BookIcon, EyeIcon } from 'lucide-react';
import { StoryGalaxyNode, getEdgesForNode, getNodeById } from '../../../data/storyGalaxyData';
import { getBookCountForTag } from '../../../utils/storyGalaxyUtils';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { getGenreColors } from '../../../utils/genreColors';
interface GravityPanelProps {
  node: StoryGalaxyNode | null;
  isOpen: boolean;
  onClose: () => void;
  selectedStackTags: string[];
  onToggleTag: (tag: string) => void;
  onViewNode?: (nodeId: string) => void;
}
export const GravityPanel: React.FC<GravityPanelProps> = ({
  node,
  isOpen,
  onClose,
  selectedStackTags,
  onToggleTag,
  onViewNode
}) => {
  const dk = useDarkClasses();
  if (!node) return null;
  // Derive a color key based on node type
  let colorKey = 'default';
  if (node.type === 'genre') colorKey = node.name;
  if (node.type === 'romance_subgenre') colorKey = 'Romance';
  if (node.type === 'story_engine') colorKey = 'Science Fiction';
  const colors = getGenreColors(colorKey);
  const edges = getEdgesForNode(node.id);
  const formatNodeType = (type: string) => {
    switch (type) {
      case 'genre':
        return 'Genre';
      case 'romance_subgenre':
        return 'Romance Subgenre';
      case 'story_engine':
        return 'Story Engine';
      default:
        return type;
    }
  };
  return <AnimatePresence data-id="element-14039" data-mp="2ykf39ks82sw3">
      {isOpen && <>
          {/* Backdrop for mobile */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden" data-id="element-14040" data-mp="2kkmjk9f45cxm" />

          {/* Panel */}
          <motion.div initial={{
        x: '100%',
        opacity: 0.5
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: '100%',
        opacity: 0.5
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className={`fixed top-0 right-0 bottom-0 w-full md:w-96 lg:w-[400px] z-50 shadow-2xl border-l flex flex-col ${dk.isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`} data-id="element-14041" data-mp="1e653zg03bb2y">
            {/* Header */}
            <div className="relative p-6 pb-8 border-b" style={{
          borderColor: dk.isDark ? '#1f2937' : '#e5e7eb'
        }} data-id="element-14042" data-mp="2n4slkbk62tbj">
              <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            background: `linear-gradient(to bottom right, ${colors.bg}, transparent)`
          }} data-id="element-14043" data-mp="ldp8zgqpv4oc" />

              <button onClick={onClose} className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${dk.isDark ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`} data-id="element-14044" data-mp="1z8xmpbcs5b0o">
                <XIcon className="h-5 w-5" data-id="element-14045" data-mp="2d1dk9wmwwgu7" />
              </button>

              <div className="relative z-10 mt-4" data-id="element-14046" data-mp="1gauuyosukzz6">
                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold mb-3" style={{
              backgroundColor: colors.bgDark,
              color: '#fff'
            }} data-id="element-14047" data-mp="1gc64z6cvz3yq">
                  {formatNodeType(node.type)}
                </div>
                <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-14048" data-mp="30qq0vhkkjr6j">
                  {node.name}
                </h2>
                <div className="flex items-center gap-1.5 mb-3" data-id="element-14049" data-mp="35ozwfta7gtso">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-14050" data-mp="348m4f0pzfmoo">
                    <BookIcon className="h-3 w-3" data-id="element-14051" data-mp="3d9ttcs6lzb63" />
                    {getBookCountForTag(node.name)} books
                  </span>
                </div>
                <p className={`text-sm font-medium mb-3 ${dk.textPrimary}`} data-id="element-14052" data-mp="399tej6flkxyi">
                  {node.shortDefinition}
                </p>
                <p className={`text-sm leading-relaxed ${dk.textSecondary}`} data-id="element-14053" data-mp="3i8wwg73137bf">
                  {node.glossaryDefinition}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-14054" data-mp="3rxyavstxdgna">
              {/* Nearby Nodes */}
              {node.nearbyNodeIds.length > 0 && <div data-id="element-14055" data-mp="9qiozy9xt0be">
                  <h3 className={`text-sm font-bold uppercase tracking-wider mb-3 ${dk.textPrimary}`} data-id="element-14056" data-mp="4ts7r7mebyoe">
                    Nearby Nodes
                  </h3>
                  <div className="flex flex-col gap-2" data-id="element-14057" data-mp="te517x6fjfbw">
                    {node.nearbyNodeIds.map(id => {
                const nearbyNode = getNodeById(id);
                if (!nearbyNode) return null;
                const isSelected = selectedStackTags.includes(nearbyNode.name);
                return <div key={id} className={`flex items-center justify-between p-2 rounded-lg border ${dk.isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-14058" data-mp="5mzovrzp756c">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-14059" data-mp="q94wtkrvpfn">
                            <span className={`text-sm font-medium truncate ${dk.textPrimary}`} data-id="element-14060" data-mp="3iwdtn75tlw9e">
                              {nearbyNode.name}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${dk.isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`} data-id="element-14061" data-mp="1tga3wkf41a6w">
                              {getBookCountForTag(nearbyNode.name)}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 shrink-0" data-id="element-14062" data-mp="2cc05i6j4s4k9">
                            {onViewNode && <button onClick={() => onViewNode(id)} className={`p-1.5 rounded-md transition-colors ${dk.isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-900'}`} title="View Node Details" data-id="element-14063" data-mp="27ermy4u2lyih">
                                <EyeIcon className="h-4 w-4" data-id="element-14064" data-mp="mwtret53zg6x" />
                              </button>}
                            <button onClick={() => onToggleTag(nearbyNode.name)} className={`p-1.5 rounded-md transition-colors ${isSelected ? 'bg-indigo-600 text-white hover:bg-indigo-700' : dk.isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} title={isSelected ? 'Remove from Stack' : 'Add to Stack'} data-id="element-14065" data-mp="33kkyqjybufps">
                              {isSelected ? <CheckIcon className="h-4 w-4" data-id="element-14066" data-mp="3vb7llwzced7d" /> : <PlusIcon className="h-4 w-4" data-id="element-14067" data-mp="1wxpi2kw6wx36" />}
                            </button>
                          </div>
                        </div>;
              })}
                  </div>
                </div>}

              {/* Gravity Edges */}
              {edges.length > 0 && <div data-id="element-14068" data-mp="1qu1dsrf21wyt">
                  <h3 className={`text-sm font-bold uppercase tracking-wider mb-3 ${dk.textPrimary}`} data-id="element-14069" data-mp="1qshyb3183py3">
                    Gravity Connections
                  </h3>
                  <div className="space-y-3" data-id="element-14070" data-mp="z3594e35eymm">
                    {edges.map(edge => {
                const targetId = edge.sourceNodeId === node.id ? edge.targetNodeId : edge.sourceNodeId;
                const targetNode = getNodeById(targetId);
                if (!targetNode) return null;
                return <div key={edge.id} className={`p-3 rounded-lg border ${dk.isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-14071" data-mp="2jk9xinp8opfi">
                          <div className="flex items-center justify-between mb-1" data-id="element-14072" data-mp="2r2hm3kqxgn9n">
                            <div className="flex items-center gap-2" data-id="element-14073" data-mp="2d2ghbu7jkvow">
                              <span className={`text-sm font-bold ${dk.textPrimary}`} data-id="element-14074" data-mp="2d4b6xltrdbzb">
                                {targetNode.name}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${dk.isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`} data-id="element-14075" data-mp="3aqgvar5ufmcy">
                                {getBookCountForTag(targetNode.name)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-14076" data-mp="3onx2i4vyrk5r">
                              <span className={`text-xs ${dk.textMuted}`} data-id="element-14077" data-mp="3ti1betosi29h">
                                Strength:
                              </span>
                              <div className="flex gap-0.5" data-id="element-14078" data-mp="2xxfnnbxcvxo9">
                                {[...Array(5)].map((_, i) => <div key={i} className={`h-1.5 w-1.5 rounded-full ${i < Math.round(edge.weight * 5) ? 'bg-indigo-500' : dk.isDark ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-14079" data-mp="2jwv6ek73a9wv" data-mpi={i} />)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-start gap-1.5 mt-2" data-id="element-14080" data-mp="2199p96r121if">
                            <LinkIcon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${dk.textMuted}`} data-id="element-14081" data-mp="25a1yhzzwg6ep" />
                            <p className={`text-xs ${dk.textSecondary}`} data-id="element-14082" data-mp="1w8c7gv9xj9hg">
                              {edge.reason}
                            </p>
                          </div>
                        </div>;
              })}
                  </div>
                </div>}

              {/* Quick Action */}
              <div className={`p-4 rounded-xl border ${dk.isDark ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-100'}`} data-id="element-14083" data-mp="1mlhqo9paufl1">
                <h4 className={`text-sm font-bold mb-1 ${dk.isDark ? 'text-indigo-300' : 'text-indigo-800'}`} data-id="element-14084" data-mp="1vkmo1cp4k2vm">
                  Build Your Stack
                </h4>
                <p className={`text-xs mb-3 ${dk.isDark ? 'text-indigo-200/70' : 'text-indigo-600/80'}`} data-id="element-14085" data-mp="25bhc8mlxlcjp">
                  Add {node.name} to your stack to find the perfect match.
                </p>
                <button onClick={() => {
              if (!selectedStackTags.includes(node.name)) {
                onToggleTag(node.name);
              }
            }} className={`w-full py-2 rounded-lg text-sm font-bold transition-colors ${selectedStackTags.includes(node.name) ? `${dk.isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-500'} cursor-default` : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'}`} disabled={selectedStackTags.includes(node.name)} data-id="element-14086" data-mp="2ec5pvg7exaw2">
                  {selectedStackTags.includes(node.name) ? 'Added to Stack' : `Add ${node.name} to Stack`}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};